import React, { useState, useEffect } from 'react';
import { Navbar, Nav, Container, Badge, NavDropdown } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart,
  faHeart,
  faUser,
  faSignOutAlt,
  faHome,
  faBoxOpen,
  faInfoCircle,
  faEnvelope,
  faBars,
  faSearch,
  faSpinner,
  faPalette,
  faMobile,
  faHeadset
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useAuth } from '../../context/AuthContext';
import { useProducts } from '../../context/ProductContext';
import { toast } from 'react-toastify';
import './Navigation.css';

function Navigation() {
  const { cart } = useCart();
  const { favorites } = useFavorites();
  const { user, handleLogout } = useAuth();
  const { phoneModels, fetchPhoneModels } = useProducts();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [mobileModelsDropdownOpen, setMobileModelsDropdownOpen] = useState(false);

  // Telefon modellerini yükle
  useEffect(() => {
    if (phoneModels.length === 0) {
      fetchPhoneModels();
    }
  }, [phoneModels.length, fetchPhoneModels]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileModelsDropdownOpen && !event.target.closest('.mobile-phone-dropdown-wrapper')) {
        setMobileModelsDropdownOpen(false);
      }
    };

    if (mobileModelsDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [mobileModelsDropdownOpen]);

  const closeMenu = () => setExpanded(false);

  const handleIconClick = (path) => {
    navigate(path);
    closeMenu();
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast.error('Lütfen telefon numaranızı girin');
      return;
    }

    // Telefon numarası formatını kontrol et
    const phoneRegex = /^5[0-9]{9}$/;
    const cleanPhone = searchQuery.replace(/\D/g, '');

    if (!phoneRegex.test(cleanPhone)) {
      toast.error('Geçerli bir telefon numarası girin (5xx xxx xx xx)');
      return;
    }

    setIsSearching(true);
    try {
      navigate(`/orders/search?phone=${encodeURIComponent(cleanPhone)}`);
    } finally {
      setIsSearching(false);
      setSearchQuery('');
    }
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setSearchQuery(value);
    }
  };

  return (
    <>
    <Navbar expanded={expanded} expand="lg" className="custom-navbar sticky-top">
      <Container>
        <Link to="/" className="brand-logo" onClick={closeMenu}>
          <img src="/images/logo.svg" alt="Mr. Brown Logo" />
          <div className="brand-text-container">
            <span className="brand-text">Mr. Brown</span>
            <span className="brand-subtitle">Premium Cases</span>
          </div>
        </Link>

        <div className="d-flex align-items-center">
          {/* Mobil görünümde hamburger menü */}
          <Navbar.Toggle 
            aria-controls="navbar-nav" 
            onClick={() => setExpanded(expanded => !expanded)}
            className="d-lg-none"
          >
            <FontAwesomeIcon icon={faBars} />
          </Navbar.Toggle>
        </div>

        <Navbar.Collapse id="navbar-nav">
          <Nav className="me-auto">
            <Link 
              to="/" 
              className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <FontAwesomeIcon icon={faHome} className="me-2" />
              Ana Sayfa
            </Link>
            <Link 
              to="/products" 
              className={`nav-link ${location.pathname === '/products' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
              Ürünler
            </Link>
            <NavDropdown
              title={
                <span className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faMobile} className="me-2" />
                  Telefon Modelleri
                </span>
              }
              id="phone-models-dropdown"
              className="phone-models-dropdown"
            >
              {phoneModels.filter(model => model.is_active).length > 0 ? (
                phoneModels
                  .filter(model => model.is_active)
                  .sort((a, b) => {
                    // Önce markaya göre, sonra modele göre sırala
                    if (a.brand !== b.brand) {
                      return a.brand.localeCompare(b.brand);
                    }
                    return a.model_name.localeCompare(b.model_name);
                  })
                  .map(model => (
                    <NavDropdown.Item
                      key={model.id}
                      onClick={() => {
                        navigate(`/products?phone_model=${model.id}`);
                        closeMenu();
                      }}
                    >
                      {model.brand} {model.model_name}
                    </NavDropdown.Item>
                  ))
              ) : (
                <NavDropdown.Item disabled>
                  <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
                  Yükleniyor...
                </NavDropdown.Item>
              )}
            </NavDropdown>
            <Link 
              to="/custom-design" 
              className={`nav-link ${location.pathname === '/custom-design' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <FontAwesomeIcon icon={faPalette} className="me-2" />
              Kendin Tasarla
            </Link>
            <Link 
              to="/about" 
              className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Hakkımızda
            </Link>
            <Link 
              to="/contact" 
              className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              İletişim
            </Link>
          </Nav>

          {/* Sipariş Arama Butonu */}
          <div className="d-flex align-items-center mx-2">
            <button 
              className="search-orders-btn"
              onClick={() => handleIconClick('/orders/search')}
            >
              <FontAwesomeIcon icon={faSearch} className="me-2" />
              Sipariş Ara
            </button>
          </div>

          <Nav>
            {/* Desktop görünümde sağ taraftaki ikonlar */}
            <div className="d-none d-lg-flex align-items-center">
              <div className="icon-container" onClick={() => handleIconClick('/favorites')}>
                <FontAwesomeIcon icon={faHeart} />
                {favorites.length > 0 && (
                  <Badge className="nav-badge">{favorites.length}</Badge>
                )}
              </div>
              <div className="icon-container" onClick={() => handleIconClick('/cart')}>
                <FontAwesomeIcon icon={faShoppingCart} />
                {cart.length > 0 && (
                  <Badge className="nav-badge">{cart.length}</Badge>
                )}
              </div>
              {user ? (
                <NavDropdown
                  title={<FontAwesomeIcon icon={faUser} />}
                  id="nav-dropdown"
                  align="end"
                >
                  <div className="user-info">
                    <div className="user-name">{user.first_name} {user.last_name}</div>
                    <div className="user-email">{user.email}</div>
                  </div>
                  <NavDropdown.Item as={Link} to="/profile" onClick={closeMenu}>
                    Profilim
                  </NavDropdown.Item>
                  {user.is_admin && (
                    <NavDropdown.Item as={Link} to="/admin" onClick={closeMenu}>
                      Admin Panel
                    </NavDropdown.Item>
                  )}
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={() => { handleLogout(); closeMenu(); }}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    Çıkış Yap
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <Link to="/login" className="nav-link" onClick={closeMenu}>
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  Giriş Yap
                </Link>
              )}
            </div>

            {/* Mobil görünümde menü içindeki kullanıcı linkleri */}
            <div className="d-lg-none">
              {user ? (
                <>
                  <div className="user-info">
                    <div className="user-name">{user.first_name} {user.last_name}</div>
                    <div className="user-email">{user.email}</div>
                  </div>
                  <Link to="/profile" className="nav-link" onClick={closeMenu}>
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    Profilim
                  </Link>
                  {user.is_admin && (
                    <Link to="/admin" className="nav-link" onClick={closeMenu}>
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      Admin Panel
                    </Link>
                  )}
                  <div 
                    className="nav-link text-danger" 
                    onClick={() => { handleLogout(); closeMenu(); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    Çıkış Yap
                  </div>
                </>
              ) : (
                <Link to="/login" className="nav-link" onClick={closeMenu}>
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  Giriş Yap
                </Link>
              )}
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>

    {/* Mobil Bottom Navigation Bar - Sadece Önemli Seçenekler */}
    <div className="mobile-bottom-nav d-lg-none">
      <div className="mobile-nav-scroll">
        <Link 
          to="/" 
          className={`mobile-nav-item ${location.pathname === '/' ? 'active' : ''}`}
          onClick={closeMenu}
        >
          <FontAwesomeIcon icon={faHome} />
          <span className="mobile-nav-label">Ana Sayfa</span>
        </Link>
        <Link 
          to="/products" 
          className={`mobile-nav-item ${location.pathname === '/products' ? 'active' : ''}`}
          onClick={closeMenu}
        >
          <FontAwesomeIcon icon={faBoxOpen} />
          <span className="mobile-nav-label">Ürünler</span>
        </Link>
        <div className="mobile-phone-dropdown-wrapper">
          <div 
            className={`mobile-nav-item mobile-phone-dropdown-toggle ${mobileModelsDropdownOpen ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setMobileModelsDropdownOpen(!mobileModelsDropdownOpen);
            }}
          >
            <FontAwesomeIcon icon={faMobile} />
            <span className="mobile-nav-label">Modeller</span>
          </div>
          {mobileModelsDropdownOpen && (
            <div className="mobile-phone-dropdown-menu">
              {phoneModels.filter(model => model.is_active).length > 0 ? (
                phoneModels
                  .filter(model => model.is_active)
                  .sort((a, b) => {
                    if (a.brand !== b.brand) {
                      return a.brand.localeCompare(b.brand);
                    }
                    return a.model_name.localeCompare(b.model_name);
                  })
                  .map(model => (
                    <div
                      key={model.id}
                      className="mobile-dropdown-item"
                      onClick={() => {
                        navigate(`/products?phone_model=${model.id}`);
                        setMobileModelsDropdownOpen(false);
                        closeMenu();
                      }}
                    >
                      {model.brand} {model.model_name}
                    </div>
                  ))
              ) : (
                <div className="mobile-dropdown-item disabled">
                  <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
                  Yükleniyor...
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mobile-nav-item" onClick={() => handleIconClick('/favorites')}>
          <FontAwesomeIcon icon={faHeart} />
          <span className="mobile-nav-label">Favoriler</span>
          {favorites.length > 0 && (
            <Badge className="mobile-nav-badge">{favorites.length}</Badge>
          )}
        </div>
        <div className="mobile-nav-item" onClick={() => handleIconClick('/cart')}>
          <FontAwesomeIcon icon={faShoppingCart} />
          <span className="mobile-nav-label">Sepet</span>
          {cart.length > 0 && (
            <Badge className="mobile-nav-badge">{cart.length}</Badge>
          )}
        </div>
        <div 
          className="mobile-nav-item"
          onClick={() => {
            // FloatingSupport butonunu tetikle
            const floatingSupport = document.querySelector('.floating-support');
            if (floatingSupport) {
              floatingSupport.click();
            }
          }}
        >
          <FontAwesomeIcon icon={faHeadset} />
          <span className="mobile-nav-label">Destek</span>
        </div>
      </div>
    </div>
    </>
  );
}

export default Navigation; 